"use strict";
import { opacifyRaw } from "ui/src/theme/color/utils";
const accentColors = {
  // Topaz brand ramp — Imperial Topaz orange (#FE3C00). The `pink*` keys are
  // historical Uniswap names repurposed to carry the brand accent.
  pinkLight: "#FFF1EC",
  pinkPastel: "#FFC9B3",
  pinkBase: "#FE3C00",
  pinkVibrant: "#C42E00",
  pinkDark: "#2A0D02",
  redLight: "#FFF2F1",
  redPastel: "#FDCFC4",
  redBase: "#FF5F52",
  redVibrant: "#FF0000",
  redDark: "#220D0C",
  orangeLight: "#FEF5EA",
  orangePastel: "#FFE8BC",
  orangeBase: "#FF8934",
  orangeVibrant: "#FF4D00",
  orangeDark: "#371B0C",
  yellowLight: "#FFFE8B",
  yellowPastel: "#FFF8B4",
  yellowBase: "#FFBF17",
  yellowVibrant: "#FFF612",
  yellowDark: "#1F1E02",
  brownLight: "#F7F6F1",
  brownPastel: "#E2E0CD",
  brownBase: "#85754A",
  brownVibrant: "#996F01",
  brownDark: "#231E0F",
  greenLight: "#EEFBF1",
  greenPastel: "#C2E7D0",
  greenBase: "#0C8911",
  greenVibrant: "#21C95E",
  absGreen: "#03d26e",
  greenDark: "#0F2C1A",
  greenVibrantLight: "#21c95f21",
  limeLight: "#F7FEEB",
  limePastel: "#E4F6C4",
  limeBase: "#78E744",
  limeVibrant: "#B1F13C",
  limeDark: "#232917",
  turquoiseLight: "#F7FEEB",
  turquoisePastel: "#CAFFDF",
  turquoiseBase: "#00C3A0",
  turquoiseVibrant: "#5CFE9D",
  turquoiseDark: "#1A2A21",
  cyanLight: "#EBF8FF",
  cyanPastel: "#B9E3F8",
  cyanBase: "#23A3FF",
  cyanVibrant: "#3ADCFF",
  cyanDark: "#15242B",
  blueLight: "#EFF4FF",
  bluePastel: "#D0D9F8",
  blueBase: "#4981FF",
  blueVibrant: "#0047FF",
  blueDark: "#10143D",
  purpleLight: "#FAF5FF",
  purplePastel: "#E9D8FD",
  purpleBase: "#9E62FF",
  purpleVibrant: "#4300B0",
  purpleDark: "#1A0040"
};
export const colors = {
  white: "#FFFFFF",
  black: "#000000",
  scrim: "rgba(0,0,0,0.60)",
  ...accentColors,
  uniswapXViolet: "#FE3C00",
  uniswapXPurple: "#C42E00",
  fiatOnRampBanner: "#FE3C00",
  // Topaz signature palette (theme-independent, for brand visuals)
  fluxBright: "#FF6A40",
  fluxMain: "#FE3C00",
  fluxDeep: "#C42E00",
  antiqueGold: "#7A1D00",
  champagne: "#FFB59A",
  obsidian: "#000000",
  graphite: "#0A0A0C",
  panelBlack: "#0E0E11",
  elevatedPanel: "#16161A",
  fluxBorder: "#26262C",
  fluxBorderMuted: "#1A1A20"
};
export const FLUX = {
  bright: "#FF6A40",
  main: "#FE3C00",
  deep: "#C42E00",
  antique: "#7A1D00",
  champagne: "#FFB59A",
  obsidian: "#000000",
  graphite: "#0A0A0C",
  panel: "#0E0E11",
  elevated: "#16161A",
  border: "#26262C",
  borderMuted: "#1A1A20"
};
export const DEP_accentColors = {
  blue200: "#ADBCFF",
  blue300: "#869EFF",
  blue400: "#4C82FB",
  gold200: "#EEB317",
  goldVibrant: "#FEB239",
  green300: "#40B66B",
  green400: "#209853",
  magenta100: "#FCE2D8",
  magenta50: "#FFF1EC",
  magentaVibrant: "#FE3C00",
  red200: "#FEA79B",
  red300: "#FD766B",
  red400: "#FA2B39",
  violet200: "#BDB8FA",
  violet400: "#7A7BEB",
  yellow100: "#F0E49A",
  yellow200: "#DBBC19"
};
export const networkColors = {
  ethereum: {
    light: "#627EEA",
    dark: "#627EEA"
  },
  optimism: {
    light: "#FF0420",
    dark: "#FF0420"
  },
  polygon: {
    light: "#8247E5",
    dark: "#8247E5"
  },
  arbitrum: {
    light: "#12AAFF",
    dark: "#12AAFF"
  },
  bnb: {
    light: "#DA3400",
    dark: "#FE3C00"
  },
  base: {
    light: "#0052FF",
    dark: "#0052FF"
  },
  blast: {
    light: "#222222",
    dark: "#FCFC03"
  },
  avalanche: {
    light: "#E84142",
    dark: "#E84142"
  },
  celo: {
    light: "#222222",
    dark: "#FCFF52"
  },
  monad: {
    light: "#836EF9",
    dark: "#836EF9"
  },
  soneium: {
    light: "#000000",
    dark: "#FFFFFF"
  },
  worldchain: {
    light: "#222222",
    dark: "#FFFFFF"
  },
  unichain: {
    light: "#fc0fa4",
    dark: "#fc0fa4"
  },
  zora: {
    light: "#222222",
    dark: "#FFFFFF"
  },
  zksync: {
    light: "#3667F6",
    dark: "#3667F6"
  }
};
const sporeLight = {
  white: colors.white,
  black: colors.black,
  scrim: colors.scrim,
  neutral1: "#0B0B0D",
  neutral1Hovered: "rgba(11, 11, 13, 0.88)",
  neutral2: "rgba(11, 11, 13, 0.64)",
  neutral2Temp: "rgba(11, 11, 13, 0.64)",
  neutral2Hovered: "rgba(11, 11, 13, 0.80)",
  neutral3: "rgba(11, 11, 13, 0.40)",
  neutral3Hovered: "rgba(11, 11, 13, 0.58)",
  surface1: "#FFFFFF",
  surface1Hovered: "#F7F7F8",
  surface2: "#F5F5F6",
  surface2Hovered: "#EEEEEF",
  surface3: "rgba(11, 11, 13, 0.06)",
  surface3Hovered: "rgba(11, 11, 13, 0.10)",
  surface3Solid: "#ECECED",
  surface4: "rgba(254, 60, 0, 0.18)",
  surface5: "rgba(254, 60, 0, 0.07)",
  slate: "#F2F2F3",
  swapBackground: "#F7F7F8",
  accent1: "#DA3400",
  accent1Hovered: "#B82B00",
  accent2: "rgba(254, 60, 0, 0.12)",
  accent2Hovered: "rgba(254, 60, 0, 0.20)",
  accent2Solid: "#FCE2D8",
  accent3: "#B82B00",
  accent3Hovered: "#8F2200",
  DEP_accentSoft: "rgba(254, 60, 0, 0.20)",
  DEP_blue400: "#4C82FB",
  absGreen: "#03d26e",
  statusSuccess: "#0A9A6E",
  statusSuccessHovered: "#067C58",
  statusSuccess2: "rgba(0, 211, 149, 0.10)",
  statusSuccess2Hovered: "rgba(0, 211, 149, 0.18)",
  statusWarning: "#9C6A12",
  statusWarningHovered: "#7E550E",
  statusWarning2: "rgba(255, 184, 77, 0.14)",
  statusWarning2Hovered: "rgba(255, 184, 77, 0.22)",
  statusCritical: "#D63A57",
  statusCriticalHovered: "#B82C46",
  statusCritical2: "rgba(255, 92, 122, 0.10)",
  statusCritical2Hovered: "rgba(255, 92, 122, 0.18)"
};
const sporeDark = {
  none: "transparent",
  white: colors.white,
  black: colors.black,
  scrim: colors.scrim,
  neutral1: "#FFFFFF",
  neutral1Hovered: "rgba(255, 255, 255, 0.88)",
  neutral2: "rgba(255, 255, 255, 0.72)",
  neutral2Temp: "rgba(255, 255, 255, 0.72)",
  neutral2Hovered: "rgba(255, 255, 255, 0.88)",
  neutral3: "rgba(255, 255, 255, 0.56)",
  neutral3Hovered: "rgba(255, 255, 255, 0.72)",
  surface1: "#000000",
  surface1Hovered: "#0D0D0F",
  surface2: "#0E0E11",
  surface2Hovered: "#16161A",
  surface3: "rgba(255, 255, 255, 0.08)",
  surface3Solid: "#1A1A1E",
  surface3Hovered: "rgba(255, 255, 255, 0.12)",
  surface4: "rgba(254, 60, 0, 0.20)",
  surface5: "rgba(255, 255, 255, 0.04)",
  slate: "#0A0A0C",
  accent1: "#FE3C00",
  accent1Hovered: "#FF5C2E",
  accent2: "rgba(254, 60, 0, 0.14)",
  accent2Hovered: "rgba(254, 60, 0, 0.24)",
  accent2Solid: "#2A0D02",
  accent3: "#FF8A5C",
  accent3Hovered: "#FFA77E",
  swapBackground: "#0A0A0C",
  DEP_accentSoft: "rgba(254, 60, 0, 0.20)",
  DEP_blue400: "#4C82FB",
  absGreen: "#03d26e",
  statusSuccess: "#00D395",
  statusSuccessHovered: "#00B07D",
  statusSuccess2: "rgba(0, 211, 149, 0.12)",
  statusSuccess2Hovered: "rgba(0, 211, 149, 0.20)",
  statusWarning: "#FFB84D",
  statusWarningHovered: "#FFC76B",
  statusWarning2: "rgba(255, 184, 77, 0.10)",
  statusWarning2Hovered: "rgba(255, 184, 77, 0.18)",
  statusCritical: "#FF5C7A",
  statusCriticalHovered: "#FF3D61",
  statusCritical2: "rgba(255, 92, 122, 0.12)",
  statusCritical2Hovered: "rgba(255, 92, 122, 0.20)"
};
export const colorsLight = {
  none: "transparent",
  white: sporeLight.white,
  black: sporeLight.black,
  scrim: sporeLight.scrim,
  neutral1: sporeLight.neutral1,
  neutral1Hovered: sporeLight.neutral1Hovered,
  neutral2: sporeLight.neutral2,
  neutral2Temp: sporeLight.neutral2Temp,
  neutral2Hovered: sporeLight.neutral2Hovered,
  neutral3: sporeLight.neutral3,
  neutral3Hovered: sporeLight.neutral3Hovered,
  surface1: sporeLight.surface1,
  surface1Hovered: sporeLight.surface1Hovered,
  surface2: sporeLight.surface2,
  surface2Hovered: sporeLight.surface2Hovered,
  surface3: sporeLight.surface3,
  surface3Solid: sporeLight.surface3Solid,
  surface3Hovered: sporeLight.surface3Hovered,
  surface4: sporeLight.surface4,
  surface5: sporeLight.surface5,
  slate: sporeLight.slate,
  accent1: sporeLight.accent1,
  accent1Hovered: sporeLight.accent1Hovered,
  accent2: sporeLight.accent2,
  accent2Solid: sporeLight.accent2Solid,
  accent2Hovered: sporeLight.accent2Hovered,
  accent3: sporeLight.accent3,
  accent3Hovered: sporeLight.accent3Hovered,
  swapBackground: sporeLight.swapBackground,
  DEP_accentSoft: sporeLight.DEP_accentSoft,
  DEP_blue400: sporeLight.DEP_blue400,
  absGreen: sporeLight.absGreen,
  statusSuccess: sporeLight.statusSuccess,
  statusSuccessHovered: sporeLight.statusSuccessHovered,
  statusSuccess2: sporeLight.statusSuccess2,
  statusSuccess2Hovered: sporeLight.statusSuccess2Hovered,
  statusCritical: sporeLight.statusCritical,
  statusCriticalHovered: sporeLight.statusCriticalHovered,
  statusCritical2: sporeLight.statusCritical2,
  statusCritical2Hovered: sporeLight.statusCritical2Hovered,
  statusWarning: sporeLight.statusWarning,
  statusWarningHovered: sporeLight.statusWarningHovered,
  statusWarning2: sporeLight.statusWarning2,
  statusWarning2Hovered: sporeLight.statusWarning2Hovered,
  DEP_backgroundBranded: "#FFF1EC",
  DEP_backgroundOverlay: opacifyRaw(60, colors.white),
  DEP_accentBranded: DEP_accentColors.magentaVibrant,
  DEP_shadowBranded: DEP_accentColors.magentaVibrant,
  DEP_brandedAccentSoft: DEP_accentColors.magenta100,
  DEP_magentaDark: opacifyRaw(12, DEP_accentColors.magentaVibrant),
  DEP_fiatBanner: colors.fiatOnRampBanner,
  chain_1: sporeLight.neutral1,
  chain_130: networkColors.unichain.light,
  chain_10: networkColors.optimism.light,
  chain_137: networkColors.polygon.light,
  chain_42161: networkColors.arbitrum.light,
  chain_80001: networkColors.polygon.light,
  chain_8453: networkColors.base.light,
  chain_7777777: networkColors.zora.light,
  chain_81457: networkColors.blast.light,
  chain_56: networkColors.bnb.light,
  chain_42220: networkColors.celo.light,
  chain_43114: networkColors.avalanche.light,
  chain_324: networkColors.zksync.light,
  chain_480: networkColors.worldchain.light,
  chain_1868: networkColors.soneium.light,
  // BNB Smart Chain Testnet (BNB mainnet 56 declared above)
  chain_97: networkColors.bnb.light,
  // Testnets
  chain_11155111: networkColors.ethereum.light,
  chain_1301: networkColors.unichain.light,
  chain_10143: networkColors.monad.light,
  pinkThemed: colors.pinkLight
};
export const colorsDark = {
  none: "transparent",
  white: sporeDark.white,
  black: sporeDark.black,
  surface1: sporeDark.surface1,
  surface1Hovered: sporeDark.surface1Hovered,
  surface2: sporeDark.surface2,
  surface2Hovered: sporeDark.surface2Hovered,
  surface3: sporeDark.surface3,
  surface3Solid: sporeDark.surface3Solid,
  surface3Hovered: sporeDark.surface3Hovered,
  surface4: sporeDark.surface4,
  surface5: sporeDark.surface5,
  slate: sporeDark.slate,
  scrim: sporeDark.scrim,
  neutral1: sporeDark.neutral1,
  neutral1Hovered: sporeDark.neutral1Hovered,
  neutral2: sporeDark.neutral2,
  neutral2Temp: sporeDark.neutral2Temp,
  neutral2Hovered: sporeDark.neutral2Hovered,
  neutral3: sporeDark.neutral3,
  neutral3Hovered: sporeDark.neutral3Hovered,
  accent1: sporeDark.accent1,
  accent1Hovered: sporeDark.accent1Hovered,
  accent2: sporeDark.accent2,
  accent2Solid: sporeDark.accent2Solid,
  accent2Hovered: sporeDark.accent2Hovered,
  accent3: sporeDark.accent3,
  accent3Hovered: sporeDark.accent3Hovered,
  swapBackground: sporeDark.swapBackground,
  DEP_accentSoft: sporeDark.DEP_accentSoft,
  DEP_blue400: sporeDark.DEP_blue400,
  absGreen: sporeDark.absGreen,
  statusSuccess: sporeDark.statusSuccess,
  statusSuccessHovered: sporeDark.statusSuccessHovered,
  statusSuccess2: sporeDark.statusSuccess2,
  statusSuccess2Hovered: sporeDark.statusSuccess2Hovered,
  statusCritical: sporeDark.statusCritical,
  statusCriticalHovered: sporeDark.statusCriticalHovered,
  statusCritical2: sporeDark.statusCritical2,
  statusCritical2Hovered: sporeDark.statusCritical2Hovered,
  statusWarning: sporeDark.statusWarning,
  statusWarningHovered: sporeDark.statusWarningHovered,
  statusWarning2: sporeDark.statusWarning2,
  statusWarning2Hovered: sporeDark.statusWarning2Hovered,
  DEP_backgroundBranded: "#0A0A0A",
  DEP_backgroundOverlay: opacifyRaw(10, colors.white),
  DEP_accentBranded: DEP_accentColors.magentaVibrant,
  DEP_shadowBranded: "#FE3C00",
  DEP_brandedAccentSoft: "#2A0D02",
  DEP_magentaDark: opacifyRaw(12, DEP_accentColors.magentaVibrant),
  DEP_fiatBanner: colors.fiatOnRampBanner,
  chain_1: sporeDark.neutral1,
  chain_130: networkColors.unichain.dark,
  chain_10: networkColors.optimism.dark,
  chain_137: networkColors.polygon.dark,
  chain_42161: networkColors.arbitrum.dark,
  chain_80001: networkColors.polygon.dark,
  chain_8453: networkColors.base.dark,
  chain_7777777: networkColors.zora.dark,
  chain_81457: networkColors.blast.dark,
  chain_56: networkColors.bnb.dark,
  chain_42220: networkColors.celo.dark,
  chain_43114: networkColors.avalanche.dark,
  chain_324: networkColors.zksync.dark,
  chain_480: networkColors.worldchain.dark,
  chain_1868: networkColors.soneium.dark,
  // BNB Smart Chain Testnet (BNB mainnet 56 declared above)
  chain_97: networkColors.bnb.dark,
  // Testnets
  chain_11155111: networkColors.ethereum.dark,
  chain_1301: networkColors.unichain.dark,
  chain_10143: networkColors.monad.dark,
  pinkThemed: colors.pinkDark
};
